import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Grid,
  Icon,
  Input,
  Loader,
  Message,
  Modal,
  Table,
} from 'semantic-ui-react';
import MotivationLayout from '../MotivationLayout';
import ConfirmSaveModal from '../ConfirmSaveModal';
import FiltersComponent from '../../../components/FiltersComponent';
import { RangeInput } from '../../../components/FiltersComponent/FilterField';
import TableStats from '../../../components/TableStats';
import {
  createLoyaltyCategory,
  deleteLoyaltyCategory,
  exportLoyaltyCategories,
  fetchLoyaltyCategories,
  updateLoyaltyCategory,
} from '../api';
import {
  defaultPageRequest,
  LoyaltyCategory,
  LoyaltyCategoryForm,
  NAME_MAX_LENGTH,
} from '../types';
import SuccessDataSaveNotify from '../../Cards/SuccessDataSaveNotify';
import { downloadBlob, getFileNameFromHeader } from '../../../shared/utils';
import CategoryMissionsModal from './CategoryMissionsModal';

const emptyForm = (): LoyaltyCategoryForm => ({
  name: '',
  minSalesMonth: '0',
  multiplier: '1.00',
  upgradeBonus: '0',
});

const CategoriesPage = () => {
  const [items, setItems] = useState<LoyaltyCategory[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<LoyaltyCategory | null>(null);
  const [form, setForm] = useState<LoyaltyCategoryForm>(emptyForm());
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof LoyaltyCategoryForm, string>>
  >({});
  const [multiplierWarning, setMultiplierWarning] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<LoyaltyCategory | null>(
    null
  );
  const [missionsCategory, setMissionsCategory] =
    useState<LoyaltyCategory | null>(null);
  const [saving, setSaving] = useState(false);

  /** K-09: backend ignores multiplier/upgradeBonus filters — apply client-side */
  const applyClientSideRangeFilters = (
    list: LoyaltyCategory[],
    filterStr?: string
  ): LoyaltyCategory[] => {
    if (!filterStr || !list.length) {
      return list;
    }
    let result = list;
    filterStr
      .split(';')
      .filter(Boolean)
      .forEach((part) => {
        const sep = part.indexOf('$');
        if (sep < 0) {
          return;
        }
        const key = part.slice(0, sep);
        const value = part.slice(sep + 1);
        if (key !== 'multiplier' && key !== 'upgradeBonus') {
          return;
        }
        const [fromRaw, toRaw] = value.split('_');
        const from =
          fromRaw === undefined || fromRaw === ''
            ? null
            : Number(fromRaw);
        const to =
          toRaw === undefined || toRaw === '' ? null : Number(toRaw);
        result = result.filter((item) => {
          const raw = (item as any)[key];
          const v = Number(raw);
          if (Number.isNaN(v)) {
            return false;
          }
          if (from !== null && !Number.isNaN(from) && v < from) {
            return false;
          }
          if (to !== null && !Number.isNaN(to) && v > to) {
            return false;
          }
          return true;
        });
      });
    return result;
  };

  const load = useCallback((filterStr?: string) => {
    setLoading(true);
    setLoadError('');
    // Request a large page so client-side multiplier/upgradeBonus filters work
    fetchLoyaltyCategories(
      { ...defaultPageRequest, size: 200 },
      filterStr
    )
      .then(({ items: list }) => {
        const raw = Array.isArray(list) ? list : [];
        const filtered = applyClientSideRangeFilters(raw, filterStr);
        setItems(filtered);
        setTotalElements(filtered.length);
      })
      .catch(() => {
        setLoadError('Не удалось загрузить категории');
        setItems([]);
        setTotalElements(0);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(filters);
  }, [load, filters]);

  const applyFilters = (filterUrl: string) => {
    setFilters(filterUrl || '');
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFieldErrors({});
    setMultiplierWarning(false);
    setFormOpen(true);
  };

  const openEdit = (item: LoyaltyCategory) => {
    setEditing(item);
    setForm({
      name: item.name || '',
      minSalesMonth: String(
        item.minSalesMonth !== undefined && item.minSalesMonth !== null
          ? item.minSalesMonth
          : 0
      ),
      multiplier: String(
        item.multiplier !== undefined && item.multiplier !== null
          ? item.multiplier
          : 1
      ),
      upgradeBonus: String(
        item.upgradeBonus !== undefined && item.upgradeBonus !== null
          ? item.upgradeBonus
          : 0
      ),
    });
    setFieldErrors({});
    setMultiplierWarning(Number(item.multiplier) > 1.2);
    setFormOpen(true);
  };

  const openDelete = (item: LoyaltyCategory) => {
    setDeletingItem(item);
    setDeleteOpen(true);
  };

  const onFieldChange = (field: keyof LoyaltyCategoryForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (field === 'multiplier') {
      const n = Number(value);
      setMultiplierWarning(!Number.isNaN(n) && n > 1.2);
    }
  };

  const validate = (): boolean => {
    const errors: Partial<Record<keyof LoyaltyCategoryForm, string>> = {};
    if (!form.name.trim()) {
      errors.name = 'Название обязательно';
    } else if (form.name.trim().length > NAME_MAX_LENGTH) {
      errors.name = `Не более ${NAME_MAX_LENGTH} символов`;
    }
    const minSales = Number(form.minSalesMonth);
    if (
      form.minSalesMonth === '' ||
      Number.isNaN(minSales) ||
      !Number.isInteger(minSales) ||
      minSales < 0
    ) {
      errors.minSalesMonth = 'Целое число ≥ 0';
    }
    const mult = Number(form.multiplier);
    if (form.multiplier === '' || Number.isNaN(mult) || mult <= 0) {
      errors.multiplier = 'Число больше 0';
    }
    const bonus = Number(form.upgradeBonus);
    if (
      form.upgradeBonus === '' ||
      Number.isNaN(bonus) ||
      !Number.isInteger(bonus) ||
      bonus < 0
    ) {
      errors.upgradeBonus = 'Целое число ≥ 0';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const requestSave = () => {
    // Force field errors to render on first Save even if inputs were never touched.
    const ok = validate();
    if (!ok) {
      return;
    }
    setConfirmOpen(true);
  };

  const doSave = () => {
    const body = {
      name: form.name.trim(),
      minSalesMonth: Number(form.minSalesMonth),
      multiplier: Number(form.multiplier),
      upgradeBonus: Number(form.upgradeBonus),
    };
    setSaving(true);
    const request =
      editing && editing.id !== undefined
        ? updateLoyaltyCategory(editing.id, body)
        : createLoyaltyCategory(body);

    request
      .then(() => {
        setConfirmOpen(false);
        setFormOpen(false);
        SuccessDataSaveNotify('Изменения сохранены');
        load(filters);
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const doDelete = () => {
    if (!deletingItem || deletingItem.id === undefined) {
      return;
    }
    setSaving(true);
    deleteLoyaltyCategory(deletingItem.id)
      .then(() => {
        setDeleteOpen(false);
        setDeletingItem(null);
        SuccessDataSaveNotify('Категория удалена');
        load(filters);
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const onExport = () => {
    setExporting(true);
    exportLoyaltyCategories()
      .then((response) => {
        const fileName = getFileNameFromHeader(
          response.headers['content-disposition'] || '',
          'loyalty-categories.csv'
        );
        const blob = new Blob([response.data]);
        downloadBlob(blob, fileName);
      })
      .catch(() => {})
      .finally(() => setExporting(false));
  };

  return (
    <MotivationLayout activeTab="categories" title="Категории лояльности">
      <Grid stackable>
        <Grid.Row>
          <Grid.Column className="table-page__stats">
            <TableStats totalElements={totalElements} />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={3}>
            <FiltersComponent
              applyFilters={applyFilters}
              totalElements={totalElements}
              render={() => (
                <React.Fragment>
                  <Button
                    className="filters__button"
                    labelPosition="right"
                    icon
                    loading={exporting}
                    disabled={exporting}
                    onClick={onExport}
                    type="button"
                  >
                    Экспорт CSV
                    <Icon name="download" />
                  </Button>
                  <Button
                    className="filters__button"
                    labelPosition="right"
                    icon
                    onClick={openCreate}
                    type="button"
                  >
                    Добавить
                    <Icon name="plus" />
                  </Button>
                </React.Fragment>
              )}
            >
              <Input type="text" name="name" placeholder="Название категории" />
              <RangeInput
                type="range"
                name="minSalesMonth"
                placeholder="Порог продаж/мес"
              />
              <RangeInput
                type="range"
                name="multiplier"
                placeholder="Коэффициент"
              />
              <RangeInput
                type="range"
                name="upgradeBonus"
                placeholder="Баллы за повышение"
              />
            </FiltersComponent>
          </Grid.Column>
          <Grid.Column width={13}>
            {loading ? (
              <Loader active inline="centered" />
            ) : loadError ? (
              <Message negative>
                <Message.Header>{loadError}</Message.Header>
                <Button
                  size="small"
                  onClick={() => load(filters)}
                  style={{ marginTop: 8 }}
                >
                  Повторить
                </Button>
              </Message>
            ) : (
              <Table celled compact selectable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Название</Table.HeaderCell>
                    <Table.HeaderCell>Порог, продаж/мес</Table.HeaderCell>
                    <Table.HeaderCell>Коэффициент</Table.HeaderCell>
                    <Table.HeaderCell>Баллы за повышение</Table.HeaderCell>
                    <Table.HeaderCell width={4}>Действия</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {items.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={5} textAlign="center">
                        Категории не найдены
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    items.map((item) => (
                      <Table.Row
                        key={item.id !== undefined ? item.id : item.name}
                      >
                        <Table.Cell
                          title={item.name}
                          style={{
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.name}
                        </Table.Cell>
                        <Table.Cell>{item.minSalesMonth}</Table.Cell>
                        <Table.Cell>{item.multiplier}</Table.Cell>
                        <Table.Cell>
                          {item.upgradeBonus !== undefined &&
                          item.upgradeBonus !== null
                            ? item.upgradeBonus
                            : '—'}
                        </Table.Cell>
                        <Table.Cell>
                          <Button size="mini" onClick={() => openEdit(item)}>
                            Изменить
                          </Button>
                          <Button
                            size="mini"
                            onClick={() => setMissionsCategory(item)}
                            disabled={item.id === undefined}
                            style={{ marginLeft: 4 }}
                          >
                            Миссии
                          </Button>
                          <Button
                            size="mini"
                            negative
                            onClick={() => openDelete(item)}
                            disabled={item.id === undefined}
                            style={{ marginLeft: 4 }}
                          >
                            Удалить
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Modal open={formOpen} onClose={() => !saving && setFormOpen(false)}>
        <Modal.Header>
          {editing ? 'Редактирование категории' : 'Новая категория'}
        </Modal.Header>
        <Modal.Content>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              requestSave();
            }}
          >
            <Form.Input
              label={`Название (макс. ${NAME_MAX_LENGTH})`}
              value={form.name}
              maxLength={NAME_MAX_LENGTH}
              error={
                fieldErrors.name
                  ? { content: fieldErrors.name, pointing: 'below' }
                  : false
              }
              onChange={(_e, { value }) => onFieldChange('name', String(value))}
            />
            <Form.Input
              label="Порог продаж в месяц"
              type="number"
              min={0}
              step={1}
              value={form.minSalesMonth}
              error={
                fieldErrors.minSalesMonth
                  ? { content: fieldErrors.minSalesMonth, pointing: 'below' }
                  : false
              }
              onChange={(_e, { value }) =>
                onFieldChange('minSalesMonth', String(value))
              }
            />
            <Form.Input
              label="Коэффициент"
              type="number"
              min={0}
              step={0.01}
              value={form.multiplier}
              error={
                fieldErrors.multiplier
                  ? { content: fieldErrors.multiplier, pointing: 'below' }
                  : false
              }
              onChange={(_e, { value }) =>
                onFieldChange('multiplier', String(value))
              }
            />
            <Form.Input
              label="Баллы за повышение категории"
              type="number"
              min={0}
              step={1}
              value={form.upgradeBonus}
              error={
                fieldErrors.upgradeBonus
                  ? { content: fieldErrors.upgradeBonus, pointing: 'below' }
                  : false
              }
              onChange={(_e, { value }) =>
                onFieldChange('upgradeBonus', String(value))
              }
            />
            {multiplierWarning ? (
              <Message
                warning
                className="motivation-page__warning"
                content="Такой коэффициент может сильно увеличить расходы на мотивацию"
              />
            ) : null}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Отмена
          </Button>
          <Button primary onClick={requestSave} disabled={saving}>
            Сохранить
          </Button>
        </Modal.Actions>
      </Modal>

      <ConfirmSaveModal
        open={confirmOpen}
        loading={saving}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doSave}
      />

      <ConfirmSaveModal
        open={deleteOpen}
        loading={saving}
        title="Удалить категорию?"
        content={
          deletingItem
            ? `Удалить категорию «${deletingItem.name}»? Это действие нельзя отменить.`
            : 'Удалить категорию?'
        }
        confirmLabel="Удалить"
        confirmNegative
        onCancel={() => {
          setDeleteOpen(false);
          setDeletingItem(null);
        }}
        onConfirm={doDelete}
      />

      <CategoryMissionsModal
        open={!!missionsCategory}
        category={missionsCategory}
        onClose={() => setMissionsCategory(null)}
      />
    </MotivationLayout>
  );
};

export default CategoriesPage;
